import React, { useState } from 'react';
import { firestore, collection, addDoc } from '../firebase.config';
import * as XLSX from 'xlsx';
import * as Papa from 'papaparse';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Box,
  Snackbar,
  Alert,
  List,
  ListItem,
  ListItemText,
  Grid,
  Paper,
  Card,
  CardContent,
  Divider
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DescriptionIcon from '@mui/icons-material/Description';
import InfoIcon from '@mui/icons-material/Info';
import DownloadIcon from '@mui/icons-material/Download';

function MembershipDataUpload({ isAdmin }) {
  const [fileData, setFileData] = useState([]);
  const [showMappingDialog, setShowMappingDialog] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [validationErrors, setValidationErrors] = useState([]);
  const [selectedFileName, setSelectedFileName] = useState('');

  // Function to convert Excel date serial number to YYYY-MM-DD format
  const excelDateToJSDate = (excelDate) => {
    if (!excelDate) return '';
    // Excel's epoch starts from 1900-01-01
    const date = new Date((excelDate - 25569) * 86400 * 1000);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${day}-${month}-${year}`;
  };

  const getNormalizedValue = (row, possibleFieldNames) => {
    // First check exact matches
    for (const field of possibleFieldNames) {
      if (row[field] !== undefined && row[field] !== '') {
        return row[field];
      }
    }
    
    // Then check for fields with extra spaces
    const keys = Object.keys(row);
    for (const key of keys) {
      const normalizedKey = key.replace(/\s+/g, ' ').trim();
      for (const field of possibleFieldNames) {
        if (normalizedKey === field) {
          return row[key];
        }
      }
    }
    
    return '';
  };

  const validateData = (data) => {
    const errors = [];
    const criticalFields = ['name', 'contactNo', 'emailAddress', 'pDate', 'validity', 'receiptNo', 'paymentDate', 'paymentAmount', 'paymentType', 'membershipCategory', 'expertise'];
    
    data.forEach((row, index) => {
      const rowErrors = [];
      
      // Check critical fields
      criticalFields.forEach(field => {
        if (!row[field]) {
          rowErrors.push(`${field} is required`);
        }
      });

      // Validate email format
      if (row.emailAddress && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(row.emailAddress)) {
        rowErrors.push('Invalid email format');
      }

      // Validate contact number
      if (row.contactNo && !/^\d{10}$/.test(row.contactNo)) {
        rowErrors.push('Contact number must be 10 digits');
      }

      if (rowErrors.length > 0) {
        errors.push({
          rowIndex: index,
          rowNumber: index + 1,
          errors: rowErrors,
          data: row
        });
      }
    });
    return errors;
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFileName(file.name);
      
      if (file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
        // Handle Excel files
        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            const data = e.target.result;
            const workbook = XLSX.read(data, { type: 'array' });
            const sheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[sheetName];
            const jsonData = XLSX.utils.sheet_to_json(worksheet, { raw: true, defval: '' });
            
            // Transform Excel data
            const transformedData = jsonData.map((row, index) => {
              // Map the Excel columns to our expected field names
              return {
                name: row['Name*'] || row['Name'] || '',
                contactNo: String(row['Contact No*'] || row['Contact No'] || ''),
                emailAddress: row['Email Address*'] || row['Email Address'] || '',
                pDate: row['Start Date* (DD-MM-YYYY)'] || row['Start Date*'] || row['Start Date'] || '',
                validity: row['End Date* (DD-MM-YYYY)'] || row['End Date*'] || row['End Date'] || '',
                receiptNo: String(row['Receipt No*'] || row['Receipt No'] || ''),
                paymentDate: row['Payment Date* (DD-MM-YYYY)'] || row['Payment Date*'] || row['Payment Date'] || '',
                paymentAmount: String(row['Payment Amount*'] || row['Payment Amount'] || ''),
                paymentType: row['Payment Type*'] || row['Payment Type'] || '',
                membershipCategory: row['Membership Category*'] || row['Membership Category'] || '',
                expertise: row['Area of Expertise*'] || row['Area of Expertise'] || '',
                remarks: row['Additional Comments'] || ''
              };
            });

            const validationErrors = validateData(transformedData);
            setValidationErrors(validationErrors);
            setFileData(transformedData);
            setShowMappingDialog(true);
          } catch (error) {
            console.error('Error processing Excel file:', error);
            setSnackbar({
              open: true,
              message: 'Error processing Excel file: ' + error.message,
              severity: 'error'
            });
          }
        };
        reader.readAsArrayBuffer(file);
      } else {
        // Handle CSV files
        Papa.parse(file, {
          complete: (results) => {
            const transformedData = results.data
              .filter(row => Object.values(row).some(value => value && value.trim() !== ''))
              .map(row => ({
                name: row['Name*'] || row['Name'] || '',
                contactNo: String(row['Contact No*'] || row['Contact No'] || ''),
                emailAddress: row['Email Address*'] || row['Email Address'] || '',
                pDate: row['Start Date* (DD-MM-YYYY)'] || row['Start Date*'] || row['Start Date'] || '',
                validity: row['End Date* (DD-MM-YYYY)'] || row['End Date*'] || row['End Date'] || '',
                receiptNo: String(row['Receipt No*'] || row['Receipt No'] || ''),
                paymentDate: row['Payment Date* (DD-MM-YYYY)'] || row['Payment Date*'] || row['Payment Date'] || '',
                paymentAmount: String(row['Payment Amount*'] || row['Payment Amount'] || ''),
                paymentType: row['Payment Type*'] || row['Payment Type'] || '',
                membershipCategory: row['Membership Category*'] || row['Membership Category'] || '',
                expertise: row['Area of Expertise*'] || row['Area of Expertise'] || '',
                remarks: row['Additional Comments'] || ''
              }));

            const validationErrors = validateData(transformedData);
            setValidationErrors(validationErrors);
            setFileData(transformedData);
            setShowMappingDialog(true);
          },
          header: true,
          skipEmptyLines: true
        });
      }
    }
  };

  const handleMappingSubmit = async () => {
    try {
      const requestsRef = collection(firestore, "MembershipRequests");
      const validRecords = fileData.filter((_, index) => 
        !validationErrors.some(error => error.rowIndex === index)
      );

      if (validRecords.length === 0) {
        setSnackbar({
          open: true,
          message: 'No valid records to submit. Please fix the errors or cancel the upload.',
          severity: 'error'
        });
        return;
      }

      for (const row of validRecords) {
        const [firstName, ...surnameArr] = (row.name || '').split(' ');
        const surname = surnameArr.join(' ');

        const memberData = {
          firstName,
          surname,
          mobileNo: row.contactNo || '',
          personalEmail: row.emailAddress || '',
          membershipStartDate: row.pDate || '',
          membershipEndDate: row.validity || '',
          additionalComments: row.remarks || '',
          paymentRef: row.receiptNo || '',
          lastPaymentDate: row.paymentDate || '',
          paymentAmount: row.paymentAmount || '',
          paymentType: row.paymentType || '',
          membershipCategory: row.membershipCategory || '',
          expertise: row.expertise || '',
          status: isAdmin ? 'approved' : 'pending',
          // Add other required fields with default values
          salutation: '',
          alternateContactNo: '',
          professionalEmail: '',
          dob: '',
          resiAddress1: '',
          resiAddress2: '',
          city: '',
          pincode: '',
          gender: '',
          qualification: '',
          organisation: '',
          noOfEmployees: '',
          designation: '',
          experience: '',
          linkedIn: '',
          facebook: '',
          instagram: '',
          referralSource: ''
        };

        if (isAdmin) {
          // If admin, directly add to MembershipMaster
          const masterRef = collection(firestore, "MembershipMaster");
          await addDoc(masterRef, memberData);
        } else {
          // If regular user, add to MembershipRequests for admin approval
          await addDoc(requestsRef, memberData);
        }
      }

      setSnackbar({
        open: true,
        message: isAdmin 
          ? `Successfully added ${validRecords.length} records to membership database`
          : `Successfully submitted ${validRecords.length} records for admin approval`,
        severity: 'success'
      });
      setShowMappingDialog(false);
      setFileData([]);
      setValidationErrors([]);
      setSelectedFileName('');
    } catch (error) {
      console.error('Error submitting membership data:', error);
      setSnackbar({
        open: true,
        message: 'Error submitting membership data: ' + error.message,
        severity: 'error'
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleDownloadTemplate = () => {
    // Create a workbook
    const wb = XLSX.utils.book_new();
    
    // Define all fields in a single row with required fields marked with *
    const templateData = [
      [
        'Name*',
        'Contact No*',
        'Email Address*',
        'Start Date* (DD-MM-YYYY)',
        'End Date* (DD-MM-YYYY)',
        'Receipt No*',
        'Payment Date* (DD-MM-YYYY)',
        'Payment Amount*',
        'Payment Type*',
        'Membership Category*',
        'Area of Expertise*',
        'Alternate Contact No',
        'Professional Email',
        'Date of Birth (DD-MM-YYYY)',
        'Resi Address 1',
        'Resi Address 2',
        'City',
        'Pincode',
        'Gender',
        'Qualification',
        'Organisation',
        'No of Employees',
        'Designation',
        'Experience',
        'LinkedIn',
        'Facebook',
        'Instagram',
        'Referral Source',
        'Additional Comments',
        'Purpose of Joining'
      ],
      // Example row with date format
      [
        'John Doe',
        '9876543210',
        'john@example.com',
        '01-01-2024',
        '31-12-2024',
        'REC123',
        '01-01-2024',
        '1000',
        'Online',
        'Individual Annual',
        'Software Development',
        '',
        '',
        '01-01-1990',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        ''
      ]
    ];

    // Create a worksheet
    const ws = XLSX.utils.aoa_to_sheet(templateData);

    // Add some styling to the worksheet
    ws['!cols'] = [
      { wch: 20 }, // Name
      { wch: 15 }, // Contact No
      { wch: 25 }, // Email Address
      { wch: 20 }, // Start Date
      { wch: 20 }, // End Date
      { wch: 15 }, // Receipt No
      { wch: 20 }, // Payment Date
      { wch: 15 }, // Payment Amount
      { wch: 15 }, // Payment Type
      { wch: 20 }, // Membership Category
      { wch: 20 }, // Area of Expertise
      { wch: 15 }, // Alternate Contact No
      { wch: 25 }, // Professional Email
      { wch: 20 }, // Date of Birth
      { wch: 20 }, // Resi Address 1
      { wch: 20 }, // Resi Address 2
      { wch: 15 }, // City
      { wch: 10 }, // Pincode
      { wch: 10 }, // Gender
      { wch: 20 }, // Qualification
      { wch: 20 }, // Organisation
      { wch: 15 }, // No of Employees
      { wch: 20 }, // Designation
      { wch: 10 }, // Experience
      { wch: 30 }, // LinkedIn
      { wch: 30 }, // Facebook
      { wch: 30 }, // Instagram
      { wch: 20 }, // Referral Source
      { wch: 30 }, // Additional Comments
      { wch: 30 }  // Purpose of Joining
    ];

    // Add some basic styling to header row
    const headerRange = XLSX.utils.decode_range(ws['!ref']);
    for (let C = headerRange.s.c; C <= headerRange.e.c; ++C) {
      const address = XLSX.utils.encode_cell({ r: 0, c: C });
      if (!ws[address]) continue;
      ws[address].s = {
        fill: { fgColor: { rgb: "FFE6F0FF" } }, // Light blue background
        font: { bold: true },
        alignment: { horizontal: "center", vertical: "center" }
      };
    }

    // Add the worksheet to the workbook
    XLSX.utils.book_append_sheet(wb, ws, "Template");

    // Generate the Excel file
    XLSX.writeFile(wb, "membership_template.xlsx");
  };

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      alignItems: 'center', 
      width: '100%',
      maxWidth: '650px',
      mx: 'auto',
      p: 3, 
      borderRadius: 2,
      bgcolor: 'background.paper',
      boxShadow: 3
    }}>
      <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: '#1976d2', mb: 2 }}>
        Membership Data Uploader
      </Typography>
      
      <Divider sx={{ width: '100%', mb: 3 }} />
      
      <Card sx={{ width: '100%', mb: 3, bgcolor: '#f5f9ff' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
            <InfoIcon sx={{ color: '#1976d2', mr: 1, mt: 0.5 }} />
            <Typography variant="body2" color="text.secondary">
              Upload membership data in bulk using Excel (.xls, .xlsx) or CSV file. 
              This tool allows you to import multiple membership records at once.
              The system will validate the data before submission.
            </Typography>
          </Box>
        </CardContent>
      </Card>
      
      <Box sx={{ 
        display: 'flex', 
        gap: 2, 
        width: '100%', 
        mb: 3 
      }}>
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={handleDownloadTemplate}
          sx={{
            flex: 1,
            py: 1.2,
            borderRadius: 2,
            textTransform: 'none',
            fontSize: '1rem',
            borderColor: '#1976d2',
            color: '#1976d2',
            '&:hover': {
              borderColor: '#1565c0',
              backgroundColor: 'rgba(25, 118, 210, 0.04)'
            }
          }}
        >
          Download Template
        </Button>

        <input
          accept=".csv,.xlsx,.xls"
          style={{ display: 'none' }}
          id="file-upload"
          type="file"
          onChange={handleFileUpload}
        />
        
        <label htmlFor="file-upload" style={{ flex: 1 }}>
          <Button
            variant="contained"
            component="span"
            startIcon={<CloudUploadIcon />}
            sx={{
              width: '100%',
              py: 1.2,
              borderRadius: 2,
              textTransform: 'none',
              fontSize: '1rem',
              boxShadow: 2,
              '&:hover': {
                boxShadow: 4
              }
            }}
          >
            Upload File
          </Button>
        </label>
      </Box>

      {selectedFileName && (
        <Box sx={{ 
          mt: 2,
          p: 1.5, 
          borderRadius: 1,
          backgroundColor: 'rgba(25, 118, 210, 0.1)',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <DescriptionIcon sx={{ mr: 1, color: '#1976d2' }} />
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {selectedFileName}
          </Typography>
        </Box>
      )}

      <Dialog 
        open={showMappingDialog} 
        onClose={() => setShowMappingDialog(false)} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: { minHeight: '60vh', maxHeight: '80vh' }
        }}
      >
        <DialogTitle sx={{ bgcolor: '#1976d2', color: 'white', pb: 2 }}>
          <Typography variant="h6">Review and Confirm Data</Typography>
          <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
            Verify imported data before submitting for approval
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom sx={{ mt: 2 }}>
            Please review the imported data before submission. The data will be sent for admin approval.
          </Typography>
          
          {validationErrors.length > 0 && (
            <Box sx={{ mt: 2, mb: 2, p: 2, bgcolor: '#fff3f3', borderRadius: 1, border: '1px solid #ffcdd2' }}>
              <Typography variant="h6" color="error" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <InfoIcon sx={{ mr: 1 }} />
                Validation Errors ({validationErrors.length} records with errors)
              </Typography>
              <List dense>
                {validationErrors.map((error, index) => (
                  <ListItem key={index}>
                    <ListItemText 
                      primary={
                        <Box>
                          <Typography variant="subtitle2" color="error">
                            Record {error.rowNumber}:
                          </Typography>
                          <List dense>
                            {error.errors.map((err, errIndex) => (
                              <ListItem key={errIndex}>
                                <ListItemText primary={err} sx={{ color: 'error.main' }} />
                              </ListItem>
                            ))}
                          </List>
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
              <Typography variant="body2" sx={{ mt: 1, color: 'error.main' }}>
                Note: Records with errors will be skipped during submission.
              </Typography>
            </Box>
          )}

          <Box sx={{ mt: 2 }}>
            {fileData && fileData.length > 0 ? (
              fileData.map((row, index) => {
                const hasErrors = validationErrors.some(error => error.rowIndex === index);
                return (
                  <Box 
                    key={index} 
                    sx={{ 
                      mb: 2, 
                      p: 2, 
                      border: '1px solid #ddd', 
                      borderRadius: 1,
                      backgroundColor: hasErrors ? '#fff3f3' : '#f5f5f5',
                      '&:hover': {
                        boxShadow: 1,
                        backgroundColor: hasErrors ? '#fff3f3' : '#f9f9f9'
                      }
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ 
                      fontWeight: 'bold', 
                      mb: 1, 
                      color: hasErrors ? 'error.main' : '#1976d2', 
                      bgcolor: hasErrors ? 'rgba(244, 67, 54, 0.1)' : 'rgba(25, 118, 210, 0.1)',
                      p: 0.8,
                      borderRadius: 1,
                      display: 'inline-block'
                    }}>
                      Record {index + 1} {hasErrors ? '(Contains Errors)' : ''}
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography><strong>Name:</strong> {row.name || 'N/A'}</Typography>
                        <Typography><strong>Contact No:</strong> {row.contactNo || 'N/A'}</Typography>
                        <Typography><strong>Email:</strong> {row.emailAddress || 'N/A'}</Typography>
                        <Typography><strong>Start Date:</strong> {row.pDate || 'N/A'}</Typography>
                        <Typography><strong>End Date:</strong> {row.validity || 'N/A'}</Typography>
                        <Typography><strong>Membership Category:</strong> {row.membershipCategory || 'N/A'}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography><strong>Expertise:</strong> {row.expertise || 'N/A'}</Typography>
                        <Typography><strong>Remarks:</strong> {row.remarks || 'N/A'}</Typography>
                        <Typography><strong>Receipt No:</strong> {row.receiptNo || 'N/A'}</Typography>
                        <Typography><strong>Payment Date:</strong> {row.paymentDate || 'N/A'}</Typography>
                        <Typography><strong>Payment Amount:</strong> {row.paymentAmount || 'N/A'}</Typography>
                        <Typography><strong>Payment Type:</strong> {row.paymentType || 'N/A'}</Typography>
                      </Grid>
                    </Grid>
                  </Box>
                );
              })
            ) : (
              <Typography variant="body1" sx={{ textAlign: 'center', py: 3 }}>
                No data to display. Please check if the file format is correct.
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, bgcolor: '#f5f5f5' }}>
          <Button 
            onClick={() => setShowMappingDialog(false)}
            sx={{ 
              color: '#666', 
              border: '1px solid #ccc',
              '&:hover': {
                bgcolor: '#f1f1f1',
                border: '1px solid #999'
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleMappingSubmit} 
            variant="contained" 
            color="primary"
            disabled={fileData.length === 0}
            sx={{
              px: 3,
              py: 1,
              textTransform: 'none',
              borderRadius: 1,
              boxShadow: 1,
              '&:hover': {
                boxShadow: 2
              },
              '&:disabled': {
                bgcolor: '#e0e0e0',
                color: '#9e9e9e'
              }
            }}
          >
            Submit Valid Records ({fileData.length - validationErrors.length} of {fileData.length})
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default MembershipDataUpload;